import React from "react";
import ReactMarkdown from "react-markdown";
import { Components } from "react-markdown";

interface RichMarkdownProps {
  children?: string | null;
  className?: string;
}

/**
 * RichMarkdown component that wraps ReactMarkdown with consistent styling
 * for proper rendering of markdown content including lists, headings, and formatting.
 */
export const RichMarkdown: React.FC<RichMarkdownProps> = ({
  children,
  className = "",
}) => {
  if (!children) return null;
  const components: Components = {
    p: ({ children }) => (
      <p
        className="mb-4 text-base md:text-lg leading-relaxed text-gray-800 break-words"
        style={{ wordWrap: "break-word", overflowWrap: "break-word" }}
      >
        {children}
      </p>
    ),
    strong: ({ children }) => (
      <strong className="font-bold text-gray-900">{children}</strong>
    ),
    em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
    h1: ({ children }) => (
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 mt-8">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 mt-6">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 mt-4">
        {children}
      </h3>
    ),
    ul: ({ children }) => (
      <ul className="list-disc list-outside mb-4 space-y-1 ml-4 pl-2">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-outside mb-4 space-y-1 ml-4 pl-2">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li
        className="text-base md:text-lg text-gray-800 break-words"
        style={{ wordWrap: "break-word", overflowWrap: "break-word" }}
      >
        {children}
      </li>
    ),
  };

  return (
    <div
      className={`prose prose-lg max-w-none w-full ${className}`}
      style={{ wordWrap: "break-word", overflowWrap: "break-word" }}
    >
      <ReactMarkdown components={components}>{children}</ReactMarkdown>
    </div>
  );
};
